import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';

const config = require('../../config.json');

export default function ContactContent() {
  const [queryDetails, setQueryDetails] = useState({ name: '', email: '', phoneNo: '', desc: '' });
  const [transactionMsg, setTransactionMsg] = useState({ text: '', isError: false });
  const [loading, setLoading] = useState(false);

  const handleContactUsQuery = async (event) => {
    event.preventDefault();
    setTransactionMsg({ text: '', isError: false });

    const { name, email, phoneNo, desc } = queryDetails;
    if (!name.trim() || !email.trim() || !desc.trim() || !phoneNo.trim()) {
      setTransactionMsg({ text: 'Please fill in all required fields.', isError: true });
      return;
    }

    setLoading(true);
    try {
      const inputParams = {
        name: name.trim(),
        phoneNo: phoneNo.trim(),
        emailID: email.trim(),
        desc: desc.trim()
      };

      const response = await axios.post(`${config.api.invokeURL}/contact-us/query`, inputParams, {
        headers: {
          'x-api-key': config.api.key
        }
      });

      if (response.status === 200) {
        setTransactionMsg({
          text: 'Thank you for your message! Our admissions and administration team will get back to you soon.',
          isError: false
        });
        setQueryDetails({ name: '', email: '', desc: '', phoneNo: '' });
      }
    } catch (error) {
      setTransactionMsg({ text: `Failed to send query: ${error.message}`, isError: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="container-fluid py-5 mb-4">
      <div className="container">
        <div className="text-center mx-auto pb-4" style={{ maxWidth: '700px' }}>
          <span className="text-burgundy fw-bold text-uppercase small" style={{ letterSpacing: '0.1em' }}>
            CONNECT WITH US
          </span>
          <h2 className="vj-card-title display-6 fw-bold mt-1 text-dark-main">
            Get in Touch with Vidyajyoti
          </h2>
          <p className="text-muted mt-2">
            Have questions about admissions, courses, research, or visiting campus? We'd love to hear from you.
          </p>
        </div>

        <div className="contact-detail p-4 p-lg-5">
          {/* Top 3 Info Cards */}
          <div className="row g-4 mb-5">
            <div className="col-lg-4 col-md-6">
              <div className="contact-card-item h-100">
                <div className="vj-icon-badge flex-shrink-0" style={{ width: '52px', height: '52px' }}>
                  <MapPin size={24} className="text-primary" />
                </div>
                <div>
                  <h6 className="fw-bold mb-1 text-dark">Campus Address</h6>
                  <a
                    href="https://goo.gl/maps/Zd4BCynmTb98ivUJ6"
                    target="_blank"
                    rel="noreferrer"
                    className="small text-muted d-block"
                  >
                    4A, Raj Niwas Marg, Civil Lines, Delhi – 110054
                  </a>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-6">
              <div className="contact-card-item h-100">
                <div className="vj-icon-badge burgundy flex-shrink-0" style={{ width: '52px', height: '52px' }}>
                  <Phone size={24} className="text-burgundy" />
                </div>
                <div>
                  <h6 className="fw-bold mb-1 text-dark">Phone Contacts</h6>
                  <a href="tel:+919971186877" className="small text-muted d-block">
                    (+91) 99711 86877
                  </a>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-12">
              <div className="contact-card-item h-100">
                <div className="vj-icon-badge flex-shrink-0" style={{ width: '52px', height: '52px' }}>
                  <Mail size={24} className="text-primary" />
                </div>
                <div>
                  <h6 className="fw-bold mb-1 text-dark">Email Enquiries</h6>
                  <a href="mailto:vidyajyotic@gmail.com" className="small text-muted d-block">
                    vidyajyotic@gmail.com
                  </a>
                  <a href="mailto:principalvj@gmail.com" className="small text-muted d-block">
                    principalvj@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Map and Form */}
          <div className="row g-4 align-items-stretch">
            {/* Map Frame */}
            <div className="col-lg-6">
              <div className="rounded-4 overflow-hidden h-100 shadow-sm border" style={{ minHeight: '380px' }}>
                <iframe
                  title="Vidyajyoti College of Theology Campus Location"
                  className="w-100 h-100"
                  style={{ border: 0, minHeight: '380px' }}
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1696.467026695403!2d77.22035555589248!3d28.67194436438119!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfda0e1b6e4eb%3A0x32051ba490c17c76!2sVidyajyoti%20College%20of%20Theology!5e0!3m2!1sen!2sin!4v1698725643824!5m2!1sen!2sin"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="col-lg-6">
              <form onSubmit={handleContactUsQuery} className="p-4 rounded-4 bg-light border h-100 d-flex flex-column justify-content-between">
                <div>
                  <h4 className="fw-bold fs-5 mb-3 text-dark">Send us a Message</h4>

                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control py-2 px-3 rounded-3 border"
                      placeholder="Your Full Name *"
                      value={queryDetails.name}
                      onChange={(e) => setQueryDetails({ ...queryDetails, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <input
                        type="email"
                        className="form-control py-2 px-3 rounded-3 border"
                        placeholder="Email Address *"
                        value={queryDetails.email}
                        onChange={(e) => setQueryDetails({ ...queryDetails, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        type="tel"
                        className="form-control py-2 px-3 rounded-3 border"
                        placeholder="Phone Number *"
                        value={queryDetails.phoneNo}
                        onChange={(e) => setQueryDetails({ ...queryDetails, phoneNo: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <textarea
                      className="form-control py-2 px-3 rounded-3 border"
                      rows="4"
                      placeholder="Write your query or message here... *"
                      value={queryDetails.desc}
                      onChange={(e) => setQueryDetails({ ...queryDetails, desc: e.target.value })}
                      required
                    />
                  </div>

                  {transactionMsg.text && (
                    <div className={`p-3 rounded-3 mb-3 small d-flex align-items-center gap-2 ${transactionMsg.isError ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'}`}>
                      {transactionMsg.isError ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
                      <span>{transactionMsg.text}</span>
                    </div>
                  )}
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    className="vj-btn-primary w-100 justify-content-center py-2"
                    disabled={loading}
                  >
                    {loading ? 'Sending Query...' : (
                      <>
                        Send Message <Send size={16} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
